/**
 * AttendanceHistoryPage
 *
 * Paginated, filterable table of attendance records for the current location.
 * Rows are clickable — tapping one opens a slide-in detail panel fetched via
 * GET /attendance/v1/{id}.
 */

import React, { useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../../../App";
import AddonGate from "../../../../components/AddonGate";
import { listAttendance, getEmployeeByUidx } from "../../../../config/Services";
import "./AttendanceHistoryPage.css";

/* ── helpers ─────────────────────────────────────────── */
function todayStr() {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function fmtTime(val) {
  if (!val) return "—";
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(String(val).trim())) return String(val).trim().slice(0, 5);
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return String(val);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return String(val);
  }
}

function fmtDateTime(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return fmtTime(val);
    return d.toLocaleString([], {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return String(val);
  }
}

function fmtPct(val) {
  if (val === null || val === undefined || val === "") return "—";
  const n = parseFloat(val);
  if (isNaN(n)) return String(val);
  return `${n.toFixed(1)}%`;
}

function fmtLatLng(lat, lng) {
  if (lat == null && lng == null) return "—";
  const f = (v) => (v != null ? parseFloat(v).toFixed(5) : "?");
  return `${f(lat)}, ${f(lng)}`;
}

const STATUS_META = {
  PUNCHED_IN: { label: "Punched In", mod: "in" },
  PUNCHED_OUT: { label: "Punched Out", mod: "out" },
  OVERTIME: { label: "Overtime", mod: "overtime" },
  HALF_DAY: { label: "Half Day", mod: "halfday" },
  ABSENT: { label: "Absent", mod: "absent" },
  WEEKLY_OFF: { label: "Weekly Off", mod: "off" },
  ON_LEAVE: { label: "On Leave", mod: "leave" },
  LATE: { label: "Late", mod: "late" },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] || { label: status || "—", mod: "unknown" };
  return <span className={`ahp-badge ahp-badge--${meta.mod}`}>{meta.label}</span>;
}

const PAGE_SIZE = 20;

/* ── Icons ───────────────────────────────────────────── */
function BackIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M19 12H5M12 5l-7 7 7 7" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" />
    </svg>
  );
}

function EmptyIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="48"
      height="48"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="4" width="18" height="18" rx="2" />
      <path d="M16 2v4M8 2v4M3 10h18M8 14h.01M12 14h.01M16 14h.01M8 18h.01M12 18h.01" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 18l6-6-6-6" />
    </svg>
  );
}

/* ── Employee-not-found banner ───────────────────────── */
function EmployeeNotFoundBanner({ isDarkMode }) {
  return (
    <div className={`ahp-emp-not-found${isDarkMode ? " dark" : ""}`}>
      <div className="ahp-emp-not-found-icon">
        <AlertIcon />
      </div>
      <div className="ahp-emp-not-found-body">
        <strong>Employee profile not linked</strong>
        <p>
          Your login account is not connected to an employee record in this location. Attendance
          history cannot be loaded. Please contact your HR administrator to link your profile.
        </p>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Detail panel
   ══════════════════════════════════════════════════════ */

/** Single info row: label + value */
function DetailRow({ label, value, highlight }) {
  return (
    <div className="adp-row">
      <span className="adp-row-label">{label}</span>
      <span className={`adp-row-value${highlight ? " adp-row-value--hl" : ""}`}>
        {value || "—"}
      </span>
    </div>
  );
}

/** Section card */
function DetailSection({ title, children }) {
  return (
    <div className="adp-section">
      <h3 className="adp-section-title">{title}</h3>
      <div className="adp-section-body">{children}</div>
    </div>
  );
}

/** Face-match percentage ring */
function MatchRing({ pct }) {
  const n = parseFloat(pct);
  const valid = !isNaN(n);
  const color = !valid ? "#9ca3af" : n >= 80 ? "#16a34a" : n >= 60 ? "#ca8a04" : "#dc2626";
  return (
    <div className="adp-match-ring" style={{ "--ring-color": color }}>
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r="30" fill="none" stroke="#e5e7eb" strokeWidth="6" />
        {valid && (
          <circle
            cx="36"
            cy="36"
            r="30"
            fill="none"
            stroke={color}
            strokeWidth="6"
            strokeDasharray={`${(n / 100) * 188.5} 188.5`}
            strokeLinecap="round"
            transform="rotate(-90 36 36)"
          />
        )}
      </svg>
      <span className="adp-match-pct" style={{ color }}>
        {valid ? `${n.toFixed(0)}%` : "—"}
      </span>
    </div>
  );
}

/** Timeline event */
function TimelineEvent({ event, isLast }) {
  const type = event?.punchType || event?.type || "";
  const isIn = /in/i.test(type);
  const time = event?.punchTime || event?.time || event?.createdAt || event?.timestamp;
  const lat = event?.latitude ?? event?.lat;
  const lng = event?.longitude ?? event?.lng ?? event?.lon;
  /* confidence is the punchLog field; also check legacy names */
  const match =
    event?.confidence ?? event?.faceMatchScore ?? event?.matchScore ?? event?.faceMatchPercentage;
  /* deviceName is the punchLog field */
  const deviceLabel = event?.deviceName || event?.deviceId || event?.device || "";
  const mode = event?.verificationMode || event?.mode || "";
  const locName = event?.locationName || "";
  const locAddr = event?.locationAddress || "";
  const workedMin = event?.totalWorkedMinutes || "";
  const status = event?.status || "";

  return (
    <div className={`adp-timeline-event${isLast ? " adp-timeline-event--last" : ""}`}>
      <div className={`adp-tl-dot${isIn ? " adp-tl-dot--in" : " adp-tl-dot--out"}`} />
      <div className="adp-tl-body">
        <div className="adp-tl-type">
          {isIn ? "Punch In" : "Punch Out"}
          {status && status !== type && (
            <span className="adp-tl-mode">{status.replace(/_/g, " ")}</span>
          )}
          {mode && <span className="adp-tl-mode">{mode}</span>}
        </div>
        <div className="adp-tl-time">{fmtTime(time)}</div>
        {locName && <div className="adp-tl-location">📍 {locName}</div>}
        {locAddr && !locName && <div className="adp-tl-location">📍 {locAddr}</div>}
        {locAddr && locName && <div className="adp-tl-address">{locAddr}</div>}
        {(lat != null || lng != null) && <div className="adp-tl-latlng">{fmtLatLng(lat, lng)}</div>}
        {match != null && (
          <div className="adp-tl-match">
            Face match: <strong>{fmtPct(match)}</strong>
          </div>
        )}
        {workedMin && <div className="adp-tl-worked">Worked this session: {workedMin}</div>}
        {deviceLabel && <div className="adp-tl-device">📱 {deviceLabel}</div>}
      </div>
    </div>
  );
}

function AttendanceDetailPanel({ record, onClose, isDarkMode }) {
  /* Lock background scroll while panel is open */
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (!record) return null;

  /* Pull fields — covers both the list-API shape and the detail-API shape */
  const r = record || {};

  const employeeName = r.employeeName || r.name || "—";
  const employeeId = r.employeeDisplayId || r.employeeId || "";
  const date = r.date || r.attendanceDate || "—";
  const status = r.attendanceStatus || r.status;

  const swipeIn = r.swipeIn || r.checkIn || r.punchInTime;
  const swipeOut = r.swipeOut || r.checkOut || r.punchOutTime;
  const workedHours = r.totalWorkedHours ?? r.workedHours ?? r.totalWorkedMinutes;
  const totalDistance = r.totalDistance || "";

  /* Face recognition — list API uses confidenceScore */
  const faceMatch =
    r.confidenceScore ??
    r.faceMatchPercentage ??
    r.faceMatchScore ??
    r.matchScore ??
    r.matchPercentage;
  const verificationMode = r.verificationMode || r.verifyMode || "";

  /* Late / early */
  const isLate = r.isLate;
  const lateByMinutes = r.lateByMinutes || "";
  const isEarly = r.isEarly;
  const earlyByMinutes = r.earlyByMinutes || "";

  /* Remarks */
  const remarks = r.remarks || "";

  /* Location — only use human-readable name fields; never fall back to IDs.
     Filter out sentinel strings the API sometimes returns (e.g. "UNKNOWN LOCATION"). */
  const _isLocationId = (v) => !v || /^[A-Z0-9_-]+$/.test(v.trim()) || /unknown/i.test(v);
  const locationName = _isLocationId(r.locationName) ? "" : r.locationName;
  const punchLocationName = _isLocationId(r.punchLocationName) ? "" : r.punchLocationName;

  /* Shift */
  const shiftName = r.shiftName || r.shift || "";
  const shiftType = r.shiftType || "";

  /* Employee details */
  const department = r.departmentName || r.department || "";
  const designation = r.designationName || r.designation || "";

  /* Verified by */
  const verifiedBy = r.verifiedBy || r.approvedBy || r.supervisorName || "";

  /* Device — list API has deviceName */
  const deviceId = r.deviceId || r.device || "";
  const deviceName = r.deviceName || "";
  const deviceType = r.deviceType || "";

  /* Timeline — punchLog is the list-API field */
  const timeline = (() => {
    if (Array.isArray(r.punchLog)) return r.punchLog; // list API ✓
    if (Array.isArray(r.punchDetails)) return r.punchDetails;
    if (Array.isArray(r.activityLogs)) return r.activityLogs;
    if (Array.isArray(r.timeline)) return r.timeline;
    if (Array.isArray(r.activityTimeline)) return r.activityTimeline;
    if (Array.isArray(r.punches)) return r.punches;
    /* Synthesise from top-level fields when no array is present */
    const synth = [];
    if (swipeIn)
      synth.push({
        type: "IN",
        time: swipeIn,
        latitude: r.checkInLatitude,
        longitude: r.checkInLongitude,
        confidence: faceMatch,
        locationName,
      });
    if (swipeOut)
      synth.push({
        type: "OUT",
        time: swipeOut,
        latitude: r.checkOutLatitude,
        longitude: r.checkOutLongitude,
        locationName,
      });
    return synth;
  })();

  return (
    <>
      {/* Overlay backdrop */}
      <div
        className={`adp-backdrop${isDarkMode ? " dark" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <div
        className={`adp-panel${isDarkMode ? " dark" : ""}`}
        role="dialog"
        aria-label="Attendance details"
      >
        {/* ── Gradient header ── */}
        <div className="adp-panel-header">
          <div className="adp-header-top">
            {/* Initials avatar */}
            <div className="adp-avatar">
              {employeeName !== "—" ? employeeName.charAt(0).toUpperCase() : "?"}
            </div>
            <div className="adp-header-info">
              <div className="adp-panel-name">{employeeName}</div>
              <div className="adp-panel-meta">
                {employeeId && <span className="adp-panel-empid">#{employeeId}</span>}
                {date !== "—" && <span className="adp-panel-date">{date}</span>}
              </div>
            </div>
            <button className="adp-close-btn" onClick={onClose} aria-label="Close details">
              <CloseIcon />
            </button>
          </div>
          {/* Status + verification mode strip */}
          <div className="adp-header-chips">
            {status && <StatusBadge status={status} />}
            {verificationMode && (
              <span className="adp-vmode-chip">{verificationMode.replace(/_/g, " ")}</span>
            )}
          </div>
        </div>

        {/* Panel body — independently scrollable */}
        <div className="adp-panel-body">
          {record && (
            <>
              {/* ── Stats cards ── */}
              <div className="adp-stats-grid">
                <div className="adp-stat-card adp-stat-card--in">
                  <span className="adp-stat-card-ico">↗</span>
                  <span className="adp-stat-card-val">{fmtTime(swipeIn)}</span>
                  <span className="adp-stat-card-lbl">Check In</span>
                </div>
                <div className="adp-stat-card adp-stat-card--out">
                  <span className="adp-stat-card-ico">↙</span>
                  <span className="adp-stat-card-val">{fmtTime(swipeOut)}</span>
                  <span className="adp-stat-card-lbl">Check Out</span>
                </div>
                <div className="adp-stat-card adp-stat-card--work">
                  <span className="adp-stat-card-ico">⏱</span>
                  <span className="adp-stat-card-val">
                    {workedHours != null ? workedHours : "—"}
                  </span>
                  <span className="adp-stat-card-lbl">Worked</span>
                </div>
                {totalDistance && (
                  <div className="adp-stat-card adp-stat-card--dist">
                    <span className="adp-stat-card-ico">📍</span>
                    <span className="adp-stat-card-val">{totalDistance}</span>
                    <span className="adp-stat-card-lbl">Distance</span>
                  </div>
                )}
              </div>

              {/* ── Late / Early banner ── */}
              {(isLate || isEarly) && (
                <div
                  className={`adp-alert-banner${
                    isLate ? " adp-alert-banner--late" : " adp-alert-banner--early"
                  }`}
                >
                  <span className="adp-alert-icon">{isLate ? "⏰" : "⚡"}</span>
                  <span>
                    {isLate && `Late by ${lateByMinutes || "some time"}`}
                    {isEarly && `Left early by ${earlyByMinutes || "some time"}`}
                  </span>
                </div>
              )}

              {/* ── Face recognition ── */}
              {faceMatch != null && (
                <div className="adp-face-card">
                  <div className="adp-face-card-title">Face Recognition</div>
                  <div className="adp-face-row">
                    <MatchRing pct={faceMatch} />
                    <div className="adp-face-meta">
                      <div className="adp-face-pct">{fmtPct(faceMatch)}</div>
                      <div className="adp-face-hint">
                        {parseFloat(faceMatch) >= 80
                          ? "✓ High confidence match"
                          : parseFloat(faceMatch) >= 60
                          ? "~ Moderate confidence"
                          : "✗ Low confidence — review needed"}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* ── Location & Shift ── */}
              <DetailSection title="Location & Shift">
                <DetailRow label="Location" value={locationName} />
                {punchLocationName && punchLocationName !== locationName && (
                  <DetailRow label="Punch at" value={punchLocationName} />
                )}
                <DetailRow label="Shift" value={shiftName} />
                {shiftType && <DetailRow label="Shift Type" value={shiftType} />}
              </DetailSection>

              {/* ── Employee details ── */}
              <DetailSection title="Employee">
                <DetailRow label="Department" value={department} />
                <DetailRow label="Designation" value={designation} />
                {verifiedBy && <DetailRow label="Verified By" value={verifiedBy} />}
              </DetailSection>

              {/* ── Device ── */}
              {(deviceName || deviceId || deviceType) && (
                <DetailSection title="Device">
                  {deviceName && <DetailRow label="Name" value={deviceName} />}
                  {deviceId && <DetailRow label="ID" value={deviceId} />}
                  {deviceType && <DetailRow label="Type" value={deviceType} />}
                </DetailSection>
              )}

              {/* ── Remarks ── */}
              {remarks && (
                <div className="adp-remarks-card">
                  <div className="adp-remarks-title">Remarks</div>
                  <p className="adp-remarks">{remarks}</p>
                </div>
              )}

              {/* ── Activity timeline ── */}
              {timeline.length > 0 && (
                <DetailSection
                  title={`Activity · ${timeline.length} event${timeline.length !== 1 ? "s" : ""}`}
                >
                  <div className="adp-timeline">
                    {timeline.map((ev, i) => (
                      <TimelineEvent key={i} event={ev} isLast={i === timeline.length - 1} />
                    ))}
                  </div>
                </DetailSection>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Inner component ─────────────────────────────────── */
function AttendanceHistoryContent() {
  const navigate = useNavigate();
  const { isDarkMode } = useContext(UserContext) || {};

  const today = todayStr();

  /* ── Employee preflight ──────────────────────────────── */
  const [empState, setEmpState] = useState({
    loading: true,
    employeeId: null,
    employeeName: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let retailUser = null;
        try {
          retailUser = JSON.parse(localStorage.getItem("retailUserDetails") || "null");
        } catch (_) {}

        const uidx = retailUser?.uidx;
        if (!uidx) {
          if (!cancelled)
            setEmpState({
              loading: false,
              employeeId: null,
              employeeName: null,
              error: "Login session data is missing. Please sign in again.",
            });
          return;
        }

        const res = await getEmployeeByUidx(uidx);
        const d = res?.data?.data?.data;

        if (!cancelled) {
          if (d?.employeeId) {
            setEmpState({
              loading: false,
              employeeId: d.employeeId,
              employeeName: d.employeeName || null,
              error: null,
            });
          } else {
            setEmpState({
              loading: false,
              employeeId: null,
              employeeName: null,
              error: "no_employee",
            });
          }
        }
      } catch (err) {
        if (!cancelled) {
          const msg =
            err?.response?.data?.data?.message ||
            err?.response?.data?.message ||
            err?.message ||
            "Could not verify your employee profile. Please try again.";
          setEmpState({ loading: false, employeeId: null, employeeName: null, error: msg });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Attendance list data ────────────────────────────── */
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const [records, setRecords] = useState([]);
  const [totalResults, setTotalResults] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const locId = localStorage.getItem("locId") || "";

  const fetchData = useCallback(
    async (p = 1) => {
      if (!empState.employeeId) return;
      setLoading(true);
      setError("");
      try {
        const payload = {
          page: p - 1,
          size: PAGE_SIZE,
          searchInput: search || "",
          locationIds: locId ? [locId] : [],
          attendanceIds: [],
          startDate,
          endDate,
          attendanceStatuses: [],
          shiftTypes: [],
          verificationModes: [],
        };

        const res = await listAttendance(payload);
        const wrapper = res?.data?.data;
        const list = Array.isArray(wrapper?.data) ? wrapper.data : [];
        const total = wrapper?.totalResults ?? list.length;
        const pages = wrapper?.totalPageNumber ?? (Math.ceil(total / PAGE_SIZE) || 1);

        setRecords(list);
        setTotalResults(total);
        setTotalPages(pages);
        setPage(p);
      } catch (err) {
        const msg =
          err?.response?.data?.data?.message ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to fetch attendance records.";
        setError(msg);
        setRecords([]);
      } finally {
        setLoading(false);
      }
    },
    [empState.employeeId, locId, startDate, endDate, search]
  );

  /* Fire on first employee resolve */
  useEffect(() => {
    if (empState.employeeId) fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empState.employeeId]);

  /* Re-fetch whenever the date range changes */
  useEffect(() => {
    if (empState.employeeId) fetchData(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData(1);
  };

  /* ── Detail panel state ──────────────────────────────── */
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);

  /* All data comes from the listing API — no secondary fetch needed */
  const openDetail = useCallback((row) => {
    setDetailOpen(true);
    setDetailRecord(row);
  }, []);

  const closeDetail = useCallback(() => {
    setDetailOpen(false);
    setDetailRecord(null);
  }, []);

  /* ── Render ──────────────────────────────────────────── */
  return (
    <div className={`ahp-root${isDarkMode ? " dark" : ""}`}>
      {/* Header */}
      <div className="ahp-header">
        <button
          className="ahp-back-btn"
          onClick={() => navigate("/products/staff-management")}
          aria-label="Back to Staff Management"
        >
          <BackIcon />
        </button>
        <div className="ahp-header-text">
          <h1>Attendance History</h1>
          <p>
            {empState.employeeName
              ? `Showing records for ${empState.employeeName}`
              : "View and search attendance records"}
          </p>
        </div>
      </div>

      {/* Body */}
      <div className="ahp-body">
        {/* Employee preflight loading */}
        {empState.loading && (
          <div className="ahp-skeleton">
            {/* Toolbar placeholder */}
            <div className="ahp-skeleton-row ahp-skeleton-toolbar">
              <div className="ahp-skeleton-cell" style={{ flex: 1.2, height: 36 }} />
              <div className="ahp-skeleton-cell" style={{ flex: 1.2, height: 36 }} />
              <div className="ahp-skeleton-cell" style={{ flex: 2, height: 36 }} />
              <div className="ahp-skeleton-cell narrow" style={{ height: 36 }} />
            </div>
            {/* Table header */}
            <div className="ahp-skeleton-row ahp-skeleton-th">
              <div className="ahp-skeleton-cell wide" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell narrow" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell narrow" style={{ height: 12 }} />
              <div className="ahp-skeleton-cell" style={{ flex: 0.3, height: 12 }} />
            </div>
            {/* Table rows */}
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="ahp-skeleton-row">
                <div className="ahp-skeleton-cell wide" />
                <div className="ahp-skeleton-cell" />
                <div className="ahp-skeleton-cell" />
                <div className="ahp-skeleton-cell" />
                <div className="ahp-skeleton-cell narrow" />
                <div className="ahp-skeleton-cell narrow" />
                <div className="ahp-skeleton-cell" style={{ flex: 0.3 }} />
              </div>
            ))}
          </div>
        )}

        {/* Employee not found / error */}
        {!empState.loading &&
          empState.error &&
          (empState.error === "no_employee" ? (
            <EmployeeNotFoundBanner isDarkMode={isDarkMode} />
          ) : (
            <div className="ahp-error">
              <span>{empState.error}</span>
              <button
                className="ahp-retry-btn"
                onClick={() =>
                  setEmpState({ loading: true, employeeId: null, employeeName: null, error: null })
                }
              >
                Retry
              </button>
            </div>
          ))}

        {/* Main content — only when employee is resolved */}
        {!empState.loading && !empState.error && (
          <>
            {/* Toolbar */}
            <form className="ahp-toolbar" onSubmit={handleSearch}>
              <div className="ahp-toolbar-left">
                <label className="ahp-field">
                  <span>From</span>
                  <input
                    type="date"
                    value={startDate}
                    max={endDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="ahp-input"
                  />
                </label>
                <label className="ahp-field">
                  <span>To</span>
                  <input
                    type="date"
                    value={endDate}
                    min={startDate}
                    max={today}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="ahp-input"
                  />
                </label>
                <label className="ahp-field ahp-search-field">
                  <span>Search</span>
                  <div className="ahp-search-wrap">
                    <SearchIcon />
                    <input
                      type="text"
                      placeholder="Search records…"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="ahp-input"
                    />
                  </div>
                </label>
              </div>
              <button type="submit" className="ahp-search-btn" disabled={loading}>
                {loading ? "Loading…" : "Search"}
              </button>
            </form>

            {/* Result count */}
            {!loading && !error && totalResults > 0 && (
              <p className="ahp-result-count">
                {totalResults} record{totalResults !== 1 ? "s" : ""} found
              </p>
            )}

            {/* Error */}
            {error && (
              <div className="ahp-error">
                <span>{error}</span>
                <button className="ahp-retry-btn" onClick={() => fetchData(page)}>
                  Retry
                </button>
              </div>
            )}

            {/* Table / skeleton / empty */}
            {loading ? (
              <div className="ahp-skeleton">
                {/* Matching header: Employee | Date | Swipe In | Swipe Out | Worked | Status | › */}
                <div className="ahp-skeleton-row ahp-skeleton-th">
                  <div className="ahp-skeleton-cell wide" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell narrow" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell narrow" style={{ height: 12 }} />
                  <div className="ahp-skeleton-cell" style={{ flex: 0.25, height: 12 }} />
                </div>
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="ahp-skeleton-row">
                    {/* Employee: two stacked lines */}
                    <div className="ahp-skeleton-cell wide ahp-skeleton-cell--double" />
                    {/* Date */}
                    <div className="ahp-skeleton-cell" />
                    {/* Swipe In */}
                    <div className="ahp-skeleton-cell" />
                    {/* Swipe Out */}
                    <div className="ahp-skeleton-cell" />
                    {/* Worked */}
                    <div className="ahp-skeleton-cell narrow" />
                    {/* Status badge */}
                    <div className="ahp-skeleton-cell narrow ahp-skeleton-badge" />
                    {/* Chevron */}
                    <div className="ahp-skeleton-cell" style={{ flex: 0.25 }} />
                  </div>
                ))}
              </div>
            ) : records.length === 0 && !error ? (
              <div className="ahp-empty">
                <EmptyIcon />
                <p>No attendance records found for this period.</p>
              </div>
            ) : records.length > 0 ? (
              <div className="ahp-table-wrap">
                <table className="ahp-table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Swipe In</th>
                      <th>Swipe Out</th>
                      <th>Worked</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {records.map((r, i) => (
                      <tr
                        key={r?.attendanceId || r?.id || i}
                        className="ahp-row-clickable"
                        onClick={() => openDetail(r)}
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && openDetail(r)}
                        role="button"
                        aria-label={`View details for ${r?.employeeName || "record"}`}
                      >
                        <td>
                          <div className="ahp-emp-cell">
                            <span className="ahp-emp-name">{r?.employeeName || "—"}</span>
                            {r?.employeeDisplayId && (
                              <span className="ahp-emp-id">{r.employeeDisplayId}</span>
                            )}
                          </div>
                        </td>
                        <td>{r?.date || "—"}</td>
                        <td>{fmtTime(r?.swipeIn)}</td>
                        <td>{fmtTime(r?.swipeOut)}</td>
                        <td>{r?.totalWorkedHours ?? r?.totalWorkedMinutes ?? "—"}</td>
                        <td>
                          <StatusBadge status={r?.attendanceStatus} />
                        </td>
                        <td className="ahp-row-chevron">
                          <ChevronRightIcon />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {/* Pagination */}
            {!loading && records.length > 0 && totalPages > 1 && (
              <div className="ahp-pagination">
                <button
                  className="ahp-page-btn"
                  disabled={page <= 1}
                  onClick={() => fetchData(page - 1)}
                >
                  ← Prev
                </button>
                <span className="ahp-page-info">
                  Page {page} of {totalPages}
                </span>
                <button
                  className="ahp-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => fetchData(page + 1)}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail panel (slide-in) */}
      {detailOpen && (
        <AttendanceDetailPanel
          record={detailRecord}
          onClose={closeDetail}
          isDarkMode={isDarkMode}
        />
      )}
    </div>
  );
}

/* ── Default export — wrapped in AddonGate ────────────── */
export default function AttendanceHistoryPage() {
  return (
    <AddonGate addonName="pallet workforce">
      <AttendanceHistoryContent />
    </AddonGate>
  );
}
